import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import PropTypes from "prop-types";
import LogoFallback from "/assets/img/bubbanlogo.jpg";
import axios from "axios";

import {
  motion,
  AnimatePresence,
} from "framer-motion";

import {
  Bell,
  CalendarDays,
  Search,
  ArrowRight,
  FileText,
  Sparkles,
  Clock,
  X,
  RefreshCw,
  ChevronRight,
} from "lucide-react";

export default function AllNotice({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Notice | Bandarban University",
}) {
  const [notices, setNotices] = useState([]);
  const [searchDate, setSearchDate] = useState("");
  const [searchTitle, setSearchTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch Notices
  const handlePost = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
      };

      const api = axios.create({
        baseURL: import.meta.env.VITE_API_BASE_URL,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const response = await api.post("/all-notice", payload);

      const data = Array.isArray(response.data)
        ? response.data
        : [];

      setNotices(data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  // Browser Tab Title
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  // Initial API Request
  useEffect(() => {
    handlePost();
  }, []);

  // Date Formatting
  function formatDate(dateStr) {
    if (!dateStr) return "Date unavailable";

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) {
      return dateStr;
    }

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  // Extract Date in YYYY-MM-DD Format
  function getDateValue(dateStr) {
    if (!dateStr) return "";

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) {
      return String(dateStr).substring(0, 10);
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  // Filter Notices
  const filteredNotices = useMemo(() => {
    let result = [...notices];

    if (searchDate) {
      result = result.filter(
        (notice) => getDateValue(notice.date) === searchDate
      );
    }

    if (searchTitle.trim()) {
      result = result.filter((notice) =>
        String(notice.head_line || "")
          .toLowerCase()
          .includes(searchTitle.toLowerCase().trim())
      );
    }

    return result;
  }, [notices, searchDate, searchTitle]);

  // Clear Filters
  const clearFilters = () => {
    setSearchDate("");
    setSearchTitle("");
  };

  const hasFilters = searchDate || searchTitle;

  // Custom Theme
  const rootStyle = {
    "--accent": accent,
    "--accent-light": `${accent}15`,
  };

  // Animation Variants
  const containerVariants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 25,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <div
      className="min-h-screen bg-[#f7faf8] overflow-hidden"
      style={rootStyle}
    >
      {/* =========================================
          HERO SECTION
      ========================================= */}

      <section
        className="relative isolate overflow-hidden"
        style={{
          background: `
            linear-gradient(
              135deg,
              ${accent} 0%,
              #0b4027 50%,
              #062c1b 100%
            )
          `,
        }}
      >
        {/* Background Decorative Shapes */}

        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 45, 0],
            }}
            transition={{
              duration: 18,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -top-32 -right-32 w-96 h-96 rounded-full border border-white/10"
          />

          <motion.div
            animate={{
              scale: [1, 1.15, 1],
              x: [0, 30, 0],
            }}
            transition={{
              duration: 14,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute top-20 -left-40 w-96 h-96 rounded-full bg-white/[0.04] blur-3xl"
          />

          <div className="absolute bottom-0 left-0 w-full h-40 bg-gradient-to-t from-black/10 to-transparent" />

          <div className="absolute inset-0 opacity-[0.04]">
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  "radial-gradient(#ffffff 1px, transparent 1px)",
                backgroundSize: "28px 28px",
              }}
            />
          </div>
        </div>

        {/* Hero Content */}

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 pt-20 pb-32 md:pt-28 md:pb-40">
          <motion.div
            initial={{
              opacity: 0,
              y: 40,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.8,
              ease: "easeOut",
            }}
            className="flex flex-col items-center text-center"
          >
            {/* University Logo */}

            <motion.div
              initial={{
                scale: 0.6,
                opacity: 0,
              }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              transition={{
                duration: 0.7,
                delay: 0.2,
                type: "spring",
                stiffness: 120,
              }}
              className="relative mb-8"
            >
              <div className="absolute -inset-4 rounded-full bg-white/10 blur-xl" />

              <div className="relative w-28 h-28 md:w-32 md:h-32 rounded-full bg-white p-3 shadow-2xl ring-4 ring-white/20">
                <img
                  src={logoSrc}
                  alt="Bandarban University Logo"
                  className="w-full h-full object-contain"
                />
              </div>

              <motion.div
                animate={{
                  rotate: 360,
                }}
                transition={{
                  duration: 15,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="absolute -inset-3 rounded-full border border-dashed border-white/30"
              />
            </motion.div>

            {/* Small Badge */}

            <motion.div
              initial={{
                opacity: 0,
                y: 15,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.35,
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-white/90 text-sm font-medium mb-6"
            >
              <Sparkles size={16} className="text-yellow-300" />

              University Information Center
            </motion.div>

            {/* Heading */}

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-tight">
              University{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-200 via-white to-green-300">
                Notices
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-base md:text-lg text-green-50/80 leading-relaxed">
              Stay informed with the latest announcements,
              academic updates, examination schedules, and
              important university notifications.
            </p>

            {/* Hero Stats */}

            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-md">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Bell size={20} className="text-green-200" />
                </div>

                <div className="text-left">
                  <p className="text-white font-bold text-xl">
                    {notices.length}
                  </p>

                  <p className="text-green-100/70 text-xs">
                    Total Notices
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-md">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <FileText size={20} className="text-green-200" />
                </div>

                <div className="text-left">
                  <p className="text-white font-bold text-xl">
                    Official
                  </p>

                  <p className="text-green-100/70 text-xs">
                    Notice Board
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Wave */}

        <div className="absolute bottom-0 left-0 w-full leading-none">
          <svg
            viewBox="0 0 1440 120"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-16 md:h-24"
            preserveAspectRatio="none"
          >
            <path
              d="M0,64 C240,120 480,0 720,48 C960,96 1200,24 1440,64 L1440,120 L0,120 Z"
              fill="#f7faf8"
            />
          </svg>
        </div>
      </section>

      {/* =========================================
          NOTICE CONTENT
      ========================================= */}

      <section className="relative py-12 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">

          {/* Section Heading */}

          <motion.div
            initial={{
              opacity: 0,
              y: 25,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.3,
            }}
            transition={{
              duration: 0.6,
            }}
            className="text-center mb-10"
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-4"
              style={{
                backgroundColor: `${accent}12`,
                color: accent,
              }}
            >
              <CalendarDays size={16} />

              Notice Board
            </div>

            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">
              Latest Announcements
            </h2>

            <p className="mt-3 text-gray-500 max-w-xl mx-auto">
              Browse all university notices and find the
              information you need.
            </p>
          </motion.div>

          {/* =========================================
              SEARCH PANEL
          ========================================= */}

          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.6,
            }}
            className="relative bg-white rounded-3xl border border-gray-100 shadow-[0_15px_50px_rgba(0,0,0,0.04)] p-5 md:p-7 mb-10"
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">

              {/* Search Heading */}

              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{
                    backgroundColor: `${accent}12`,
                    color: accent,
                  }}
                >
                  <Search size={23} />
                </div>

                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Find a Notice
                  </h3>

                  <p className="text-sm text-gray-500">
                    Search by date or notice title
                  </p>
                </div>
              </div>

              {/* Search Inputs */}

              <div className="flex flex-col sm:flex-row gap-3 w-full lg:max-w-2xl">

                {/* Date Search */}

                <div className="relative w-full sm:w-1/2">
                  <CalendarDays
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />

                  <input
                    type="date"
                    value={searchDate}
                    onChange={(e) =>
                      setSearchDate(e.target.value)
                    }
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-700 text-sm outline-none transition-all focus:bg-white focus:ring-4 focus:ring-green-100 focus:border-green-600"
                  />
                </div>

                {/* Title Search */}

                <div className="relative w-full sm:w-1/2">
                  <Search
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />

                  <input
                    type="text"
                    placeholder="Search by title..."
                    value={searchTitle}
                    onChange={(e) =>
                      setSearchTitle(e.target.value)
                    }
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-700 text-sm outline-none transition-all focus:bg-white focus:ring-4 focus:ring-green-100 focus:border-green-600"
                  />
                </div>
              </div>
            </div>

            {/* Filter Status */}

            <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-5 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: accent,
                  }}
                />

                Showing{" "}
                <span className="font-bold text-gray-900">
                  {filteredNotices.length}
                </span>{" "}
                of{" "}
                <span className="font-bold text-gray-900">
                  {notices.length}
                </span>{" "}
                notices
              </div>

              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-red-500 hover:text-red-700 transition"
                >
                  <X size={15} />

                  Clear Filters
                </button>
              )}
            </div>
          </motion.div>

          {/* =========================================
              LOADING STATE
          ========================================= */}

          {loading && (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  className="animate-pulse bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-5"
                >
                  <div className="w-16 h-16 rounded-2xl bg-gray-200 shrink-0" />

                  <div className="flex-1 space-y-3">
                    <div className="h-3 bg-gray-200 rounded w-1/4" />

                    <div className="h-5 bg-gray-200 rounded w-3/4" />
                  </div>

                  <div className="hidden sm:block w-24 h-10 bg-gray-200 rounded-xl" />
                </div>
              ))}
            </div>
          )}

          {/* =========================================
              ERROR STATE
          ========================================= */}

          {!loading && error && (
            <motion.div
              initial={{
                opacity: 0,
                scale: 0.95,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              className="bg-white rounded-3xl border border-red-100 p-10 text-center shadow-sm"
            >
              <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
                <RefreshCw
                  size={28}
                  className="text-red-500"
                />
              </div>

              <h3 className="text-xl font-bold text-gray-900">
                Unable to Load Notices
              </h3>

              <p className="text-gray-500 mt-2 mb-6">
                {error}
              </p>

              <button
                onClick={handlePost}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold transition hover:opacity-90"
                style={{
                  backgroundColor: accent,
                }}
              >
                <RefreshCw size={17} />

                Try Again
              </button>
            </motion.div>
          )}

          {/* =========================================
              NOTICE LIST
          ========================================= */}

          {!loading && !error && filteredNotices.length > 0 && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-4"
            >
              {filteredNotices.map((notice, index) => (
                <motion.div
                  key={notice.id || index}
                  variants={itemVariants}
                  layout
                  whileHover={{
                    y: -4,
                  }}
                  transition={{
                    duration: 0.25,
                  }}
                  className="group relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-green-200 transition-all duration-300 overflow-hidden"
                >
                  {/* Hover Accent */}

                  <div
                    className="absolute left-0 top-0 bottom-0 w-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      backgroundColor: accent,
                    }}
                  />

                  <div className="flex flex-col sm:flex-row sm:items-center gap-5 p-5 md:p-6">

                    {/* Date Card */}

                    <div
                      className="relative flex sm:flex-col items-center justify-center gap-3 sm:gap-1 w-full sm:w-20 h-auto sm:h-20 min-h-16 rounded-2xl shrink-0 px-4 sm:px-2 py-3 sm:py-2"
                      style={{
                        backgroundColor: `${accent}10`,
                        color: accent,
                      }}
                    >
                      <CalendarDays
                        size={20}
                        className="sm:hidden"
                      />

                      <span className="text-xs font-semibold uppercase tracking-wider">
                        {notice.date
                          ? new Date(
                              notice.date
                            ).toLocaleDateString("en-GB", {
                              month: "short",
                            })
                          : "Notice"}
                      </span>

                      <span className="text-xl sm:text-2xl font-extrabold">
                        {notice.date
                          ? new Date(
                              notice.date
                            ).toLocaleDateString("en-GB", {
                              day: "2-digit",
                            })
                          : "--"}
                      </span>

                      <span className="text-xs font-medium">
                        {notice.date
                          ? new Date(
                              notice.date
                            ).getFullYear()
                          : ""}
                      </span>
                    </div>

                    {/* Notice Information */}

                    <div className="flex-1 min-w-0">

                      {/* Notice Metadata */}

                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <span
                          className="inline-flex items-center gap-1.5 text-xs font-semibold"
                          style={{
                            color: accent,
                          }}
                        >
                          <Clock size={13} />

                          {formatDate(notice.date)}
                        </span>

                        <span className="w-1 h-1 rounded-full bg-gray-300" />

                        <span className="text-xs text-gray-400 font-medium">
                          Official Announcement
                        </span>
                      </div>

                      {/* Notice Title */}

                      <h3 className="text-base md:text-lg font-bold text-gray-800 leading-relaxed group-hover:text-green-800 transition-colors duration-300 break-words">
                        {notice.head_line}
                      </h3>

                      {/* Short Description */}

                      <p className="text-sm text-gray-400 mt-2">
                        Click to view the complete notice and
                        related information.
                      </p>
                    </div>

                    {/* Details Button */}

                    <div className="shrink-0">
                      <Link
                        to={`/notice/${notice.id}`}
                        className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-3 rounded-xl text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:shadow-lg hover:gap-3"
                        style={{
                          backgroundColor: accent,
                        }}
                      >
                        Details

                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* =========================================
              EMPTY STATE
          ========================================= */}

          {!loading &&
            !error &&
            filteredNotices.length === 0 && (
              <AnimatePresence>
                <motion.div
                  initial={{
                    opacity: 0,
                    y: 20,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  className="bg-white rounded-3xl border border-gray-100 shadow-sm py-16 px-6 text-center"
                >
                  <motion.div
                    animate={{
                      y: [0, -8, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="w-24 h-24 rounded-3xl bg-green-50 flex items-center justify-center mx-auto mb-6"
                  >
                    <FileText
                      size={42}
                      className="text-green-700"
                    />
                  </motion.div>

                  <h3 className="text-2xl font-bold text-gray-900">
                    No Notices Found
                  </h3>

                  <p className="text-gray-500 mt-3 max-w-md mx-auto">
                    {hasFilters
                      ? "We couldn't find any notices matching your search. Try another date or keyword."
                      : "There are currently no notices available. Please check back later."}
                  </p>

                  {hasFilters && (
                    <button
                      onClick={clearFilters}
                      className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition hover:opacity-90"
                      style={{
                        backgroundColor: accent,
                      }}
                    >
                      <RefreshCw size={16} />

                      Clear Search
                    </button>
                  )}
                </motion.div>
              </AnimatePresence>
            )}

          {/* =========================================
              BOTTOM INFORMATION
          ========================================= */}

          {!loading && !error && notices.length > 0 && (
            <motion.div
              initial={{
                opacity: 0,
              }}
              whileInView={{
                opacity: 1,
              }}
              viewport={{
                once: true,
              }}
              className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 p-5 rounded-2xl bg-white border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    backgroundColor: `${accent}12`,
                    color: accent,
                  }}
                >
                  <Bell size={19} />
                </div>

                <div>
                  <p className="font-semibold text-gray-800 text-sm">
                    Stay Updated
                  </p>

                  <p className="text-xs text-gray-500 mt-1">
                    Check the university notice board regularly.
                  </p>
                </div>
              </div>

              <div
                className="text-sm font-semibold flex items-center gap-2"
                style={{
                  color: accent,
                }}
              >
                Bandarban University

                <ChevronRight size={16} />
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}

AllNotice.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
  pageTitle: PropTypes.string,
};